import styles from './SchemeNode.module.css';

import qualityControlIcon from 'assets/svg/quality-control-01.svg';
import moreVerticalIcon from 'assets/svg/more-vertical-01.svg';
import deleteIcon from '../../../assets/svg/delete-01.svg';
import storageIcon from '../../../assets/svg/storage-01.svg';

import { address_server_short, PUBLIC_ASSETS } from '../../../config';
import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import ProtocolHandler from './ProtocolHandler';
import DevicesHandler from './DevicesHandler';
import {
  SchemeModes,
  SchemeNodeOperationTypes,
  SchemeNodeTypes,
  SchemeObjectTypes,
  StatusIteration,
} from '../../../consts/scheme.consts';
import Icon from '../../../helpers/components/Icon';
import InsertSchemeNodeButton from '../SchemeButtons/InsertSchemeNodeButton';
import QualityControlHandler from './QualityControlHandler';
import CommonAddMenu from '../SchemeMenus/CommonAddMenu';
import useContextMenu from '../../../hooks/useContextMenu';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { schemesStore } from '../../../store/mobx/schemes.store';
import ObjectSelectMenu from '../SchemeMenus/ObjectSelectMenu';
import { useSchemeEditorContext } from '../SchemeEditorProvider';
import SampleTypeWrapper from 'components/SampleTypeWrapper';

const NODE_MENU_OPTIONS = [
  { title: 'Добавить объект', value: SchemeNodeOperationTypes.addObject },
  { title: 'Добавить этап', value: SchemeNodeOperationTypes.addNode },
  { title: 'Удалить этап', value: SchemeNodeOperationTypes.removeNode },
  { title: 'Убрать контроль качества', value: SchemeNodeOperationTypes.removeQualityControl },
];

const SchemeNode = ({
  className,
  item,
  parentStatus = undefined,
  useStatusIndication = false,
  loadingDeviceType = 'model',
}) => {
  const [
    nodeOptionsMenuOpen,
    nodeOptionsMenuPosition,
    openNodeOptionsMenu,
    closeNodeOptionsMenu,
    nodeOptionsMenuRef,
  ] = useContextMenu();

  const { removeSchemeNode, upsertSchemeNode } = schemesStore;
  const { schemeMode } = useSchemeEditorContext();

  useOutsideClick(nodeOptionsMenuRef, closeNodeOptionsMenu);

  const [qualityControl, setQualityControl] = React.useState(null);
  const [childrenNodes, setChildrenNodes] = React.useState([]);
  const [isQualityControlNode, setIsQualityControlNode] = React.useState(false);
  const [menuOptions, setMenuOptions] = React.useState(NODE_MENU_OPTIONS);
  const [addObjectMenuOpen, setAddObjectMenuOpen] = React.useState(false);
  const [addNodeMenuOpen, setAddNodeMenuOpen] = React.useState(false);

  useEffect(() => {
    setQualityControl(
      item.children?.find((child) => child.type === SchemeNodeTypes.qualityControl),
    );
    setChildrenNodes(
      item.children?.filter((child) => child.type !== SchemeNodeTypes.qualityControl),
    );
    setIsQualityControlNode(item.type === SchemeNodeTypes.qualityControl);
  }, [item]);

  const getBackgroundColorByStatus = (status) =>
    useStatusIndication
      ? {
          [styles.red]: status == StatusIteration.NotStarted || status === undefined,
          [styles.green]: status == StatusIteration.Completed,
          [styles.orange]: status == StatusIteration.InProgress,
        }
      : undefined;

  const getBorderColorByStatus = (status) =>
    useStatusIndication
      ? {
          [styles.redBorder]: status == StatusIteration.NotStarted || status === undefined,
          [styles.greenBorder]: status == StatusIteration.Completed,
          [styles.orangeBorder]: status == StatusIteration.InProgress,
        }
      : undefined;

  const getBackgroundBeforeColorByStatus = (status) =>
    useStatusIndication
      ? {
          [styles.redBefore]: status == StatusIteration.NotStarted || status === undefined,
          [styles.greenBefore]: status == StatusIteration.Completed,
          [styles.orangeBefore]: status == StatusIteration.InProgress,
        }
      : undefined;

  useEffect(() => {
    setMenuOptions(() => {
      let newOptions = [...NODE_MENU_OPTIONS];
      if (!item.quality?.id) {
        newOptions = newOptions.filter(
          (action) => action.value !== SchemeNodeOperationTypes.removeQualityControl,
        );
      }
      if (item.type === SchemeNodeTypes.storage) {
        newOptions = newOptions.filter(
          (action) =>
            action.value !== SchemeNodeOperationTypes.addNode &&
            action.value !== SchemeNodeOperationTypes.addObject,
        );
      }
      if (item.children.some((child) => child.type === SchemeNodeTypes.storage)) {
        newOptions = newOptions.filter(
          (action) => action.value !== SchemeNodeOperationTypes.addNode,
        );
      }
      return newOptions;
    });
  }, [qualityControl, childrenNodes, item]);

  const handleNodeOptionsMenuActionClick = async (action) => {
    const actionsCallbacks = {
      [SchemeNodeOperationTypes.addObject]: () => setAddObjectMenuOpen(true),
      [SchemeNodeOperationTypes.addNode]: () => setAddNodeMenuOpen(true),
      [SchemeNodeOperationTypes.removeNode]: () => removeSchemeNode(item.id),
      [SchemeNodeOperationTypes.removeQualityControl]: () => handleRemoveQuality(),
    };

    await actionsCallbacks[action?.value]?.();
    closeNodeOptionsMenu();
  };

  const handleAddObjectActionClick = async (object) => {
    setAddObjectMenuOpen(false);
    if (!object.type) return;
    await upsertSchemeNode({
      ...item,
      objects: [
        ...item.objects,
        {
          id: object.value,
          type: object.type,
          ...(typeof object.derived === 'boolean'
            ? {
                derived: object.derived,
              }
            : {}),
        },
      ],
    });
  };

  const handleRemoveQuality = async () => {
    await upsertSchemeNode({
      ...item,
      quality: null,
    });
  };

  const handleAddNodeActionClick = async (object) => {
    setAddNodeMenuOpen(false);
    if (object.node !== SchemeNodeTypes.storage && !object.type) return;
    return await upsertSchemeNode({
      id_scheme: item.id_scheme,
      id_parent: item.id,
      type: object.node,
      objects: object.type
        ? [
            object.type && {
              id: object.value,
              type: object.type,
              ...(typeof object.derived === 'boolean'
                ? {
                    derived: object.derived,
                  }
                : {}),
            },
          ]
        : [],
      resources: [],
    });
  };

  const handleDeleteObject = async (object) => {
    await upsertSchemeNode({
      ...item,
      objects: [...item.objects.filter((itemObject) => itemObject.id !== object.id)],
    });
  };

  const NodeMenuOptions = () => (
    <div className={styles.optionsMenuContainer}>
      <div
        className={classNames(
          styles.containerImg,
          styles.optionsMenu,
          getBorderColorByStatus(item.status),
        )}
        onClick={openNodeOptionsMenu}
      >
        <Icon
          src={moreVerticalIcon}
          color={'var(--border-gray)'}
          width={'20px'}
          height={'var(--image-size)'}
          className={classNames(styles.optionsImage)}
        />
      </div>
    </div>
  );

  const Object = ({ object }) => (
    <SampleTypeWrapper object={object}>
      <div className={styles.objectItem}>
        <div className={styles.objectIconContainer}>
          <div
            className={classNames(
              styles.containerImg,
              styles.containerImgSize,
              styles.object,
              getBorderColorByStatus(item.status),
              {
                [styles.derivedBorder]:
                  !object?.derived &&
                  !useStatusIndication &&
                  object.type === SchemeObjectTypes.typeSamples,
              },
            )}
          >
            <img
              src={address_server_short + PUBLIC_ASSETS + object.image}
              className={styles.itemImg}
              alt="N/A"
            />
            {schemeMode === SchemeModes.edit && item?.objects?.length > 1 && (
              <div className={styles.overlay} onClick={() => handleDeleteObject(object)}>
                <Icon
                  src={deleteIcon}
                  color={'var(--color-brand)'}
                  width={'17px'}
                  height={'17px'}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.objectTitle}>{object.name}</div>
      </div>
    </SampleTypeWrapper>
  );

  const Storage = () => (
    <div className={styles.objectItem}>
      <div className={styles.objectIconContainer}>
        <div
          className={classNames(
            styles.containerImg,
            styles.containerImgSize,
            styles.object,
            getBorderColorByStatus(item.status),
          )}
        >
          <img src={storageIcon} className={styles.itemImg} alt="N/A" />
        </div>
      </div>
      <div className={styles.objectTitle}>Хранилище</div>
    </div>
  );

  const Resources = () =>
    !isQualityControlNode && (
      <div className={styles.resourceContainer}>
        <div className={classNames(styles.divider, getBackgroundColorByStatus(item.status))} />
        <div className={styles.resourceActions}>
          {childrenNodes && childrenNodes.length > 0 && (
            <>
              <ProtocolHandler node={item} />
              <DevicesHandler node={item} mode={loadingDeviceType} />
            </>
          )}
          {item.type === SchemeNodeTypes.sample && <QualityControlHandler node={item} />}
        </div>
      </div>
    );

  const childrenNodesListMemo = useMemo(
    () =>
      childrenNodes &&
      childrenNodes?.length > 0 && (
        <>
          <div className={classNames(styles.childNodes)}>
            {childrenNodes.map(
              (child, index) =>
                (child.type === SchemeNodeTypes.sample ||
                  child.type === SchemeNodeTypes.storage) && (
                  <SchemeNode
                    loadingDeviceType={loadingDeviceType}
                    key={index}
                    item={child}
                    parentStatus={item.status}
                    useStatusIndication={useStatusIndication}
                    className={classNames(
                      styles.childNode,
                      {
                        [styles.notLastChild]:
                          childrenNodes?.length > 1 && index !== childrenNodes?.length - 1,
                      },
                      getBackgroundBeforeColorByStatus(item.status),
                    )}
                  />
                ),
            )}
          </div>
        </>
      ),
    [childrenNodes],
  );

  return (
    <div className={classNames(styles.nodeContainer, className)}>
      <div className={styles.nodeDetails}>
        {schemeMode === SchemeModes.edit && !isQualityControlNode && <NodeMenuOptions />}
        <div>
          <div
            className={classNames(
              styles.objectContainer,
              {
                [styles.containsQualityControl]: !!qualityControl,
                [styles.qualityControlObject]: isQualityControlNode,
              },
              getBackgroundBeforeColorByStatus(parentStatus),
            )}
          >
            {item.type !== SchemeNodeTypes.qualityControl &&
              item.objects?.map((object, index) => <Object key={index} object={object} />)}
            {item.type === SchemeNodeTypes.storage && <Storage />}
            {nodeOptionsMenuOpen && (
              <CommonAddMenu
                ref={nodeOptionsMenuRef}
                title={'Действия'}
                actions={menuOptions}
                position={{ ...nodeOptionsMenuPosition }}
                open={nodeOptionsMenuOpen}
                onSelect={handleNodeOptionsMenuActionClick}
              />
            )}
            {addObjectMenuOpen && (
              <ObjectSelectMenu
                onSelect={handleAddObjectActionClick}
                onClose={() => setAddObjectMenuOpen(false)}
                node={item}
                filterMenu={true}
              />
            )}
            {addNodeMenuOpen && (
              <ObjectSelectMenu
                onSelect={handleAddNodeActionClick}
                onClose={() => setAddNodeMenuOpen(false)}
                node={item}
              />
            )}
          </div>
          {(childrenNodes.length > 0 || schemeMode === SchemeModes.edit) && <Resources />}
        </div>
        {qualityControl && (
          <div>
            <SchemeNode
              loadingDeviceType={loadingDeviceType}
              item={qualityControl}
              className={classNames('')}
              parentStatus={item.status}
              useStatusIndication={useStatusIndication}
            />
          </div>
        )}
      </div>
      {childrenNodesListMemo}
      {schemeMode === SchemeModes.edit &&
        !childrenNodes.length &&
        !isQualityControlNode &&
        item.type == SchemeNodeTypes.sample && <InsertSchemeNodeButton parentNode={item} />}
    </div>
  );
};

export default SchemeNode;
